import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Block as SuspendIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import {
  getChef,
  getAdminChefDashboard,
  getAdminChefRecipes,
  approveChef,
  rejectChef,
  suspendChef,
  recordChefPayment,
  Chef,
  AdminChefDashboard,
  ChefRecipe,
} from '../adminApi';

const ChefDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [chef, setChef] = useState<Chef | null>(null);
  const [dashboard, setDashboard] = useState<AdminChefDashboard | null>(null);
  const [recipes, setRecipes] = useState<ChefRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Action dialogs
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [paymentYear, setPaymentYear] = useState(new Date().getFullYear());
  const [paymentReference, setPaymentReference] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [chefRes, dashboardRes, recipesRes] = await Promise.all([
        getChef(parseInt(id)),
        getAdminChefDashboard(parseInt(id)).catch(() => null),
        getAdminChefRecipes(parseInt(id)).catch(() => []),
      ]);
      setChef(chefRes.chef);
      setDashboard(dashboardRes);
      setRecipes(recipesRes);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load chef');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusChip = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      active: 'success',
      approved: 'info',
      pending: 'warning',
      suspended: 'error',
      rejected: 'error',
    };
    return <Chip label={status} color={colors[status] || 'default'} />;
  };

  const handleCopyCode = () => {
    if (chef?.promo_code) {
      navigator.clipboard.writeText(chef.promo_code);
      setSuccessMessage('Promo code copied!');
    }
  };

  const handleApprove = async () => {
    if (!chef) return;
    setActionLoading(true);
    setError(null);
    try {
      await approveChef(chef.chef_id);
      setSuccessMessage(`${chef.name} has been approved!`);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve chef');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!chef || !actionReason) return;
    setActionLoading(true);
    setError(null);
    try {
      await rejectChef(chef.chef_id, actionReason);
      setSuccessMessage(`${chef.name} has been rejected.`);
      setRejectDialogOpen(false);
      setActionReason('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject chef');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!chef || !actionReason) return;
    setActionLoading(true);
    setError(null);
    try {
      await suspendChef(chef.chef_id, actionReason);
      setSuccessMessage(`${chef.name} has been suspended.`);
      setSuspendDialogOpen(false);
      setActionReason('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to suspend chef');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!chef || !paymentReference) return;
    setActionLoading(true);
    setError(null);
    try {
      await recordChefPayment(chef.chef_id, paymentYear, paymentReference);
      setSuccessMessage(`Payment recorded for ${paymentYear}.`);
      setPaymentDialogOpen(false);
      setPaymentReference('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!chef) {
    return <Alert severity="error">Chef not found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/admin/chefs')}>
          <BackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" fontWeight="bold">
            {chef.name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
            {getStatusChip(chef.status)}
            {chef.slug && (
              <Typography variant="body2" color="text.secondary">
                /{chef.slug}
              </Typography>
            )}
            {chef.promo_code && (
              <Tooltip title="Copy promo code">
                <Chip
                  label={chef.promo_code}
                  size="small"
                  variant="outlined"
                  onClick={handleCopyCode}
                  icon={<CopyIcon fontSize="small" />}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          {chef.status === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={handleApprove}
                disabled={actionLoading}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => setRejectDialogOpen(true)}
                disabled={actionLoading}
              >
                Reject
              </Button>
            </>
          )}
          {(chef.status === 'approved' || chef.status === 'active') && (
            <>
              <Button
                variant="outlined"
                startIcon={<PaymentIcon />}
                onClick={() => setPaymentDialogOpen(true)}
                disabled={actionLoading}
              >
                Record Payment
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<SuspendIcon />}
                onClick={() => setSuspendDialogOpen(true)}
                disabled={actionLoading}
              >
                Suspend
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Info Section */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Contract Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contract Details
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2">{chef.email}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Tier</Typography>
                <Typography variant="body2">{chef.tier || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Contract Start</Typography>
                <Typography variant="body2">{formatDate(chef.contract_start_date)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Contract End</Typography>
                <Typography variant="body2">{formatDate(chef.contract_end_date)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Recipe Rate</Typography>
                <Typography variant="body2">${chef.recipe_rate ?? 30}/recipe/year</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">User ID</Typography>
                <Typography variant="body2">{chef.user_id || 'Not linked'}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Approved</Typography>
                <Typography variant="body2">{formatDate(chef.approved_at)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{formatDate(chef.created_at)}</Typography>
              </Grid>
            </Grid>
            {chef.bio && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">Bio</Typography>
                <Typography variant="body2">{chef.bio}</Typography>
              </Box>
            )}
            {chef.credentials && (
              <Box mt={1}>
                <Typography variant="caption" color="text.secondary">Credentials</Typography>
                <Typography variant="body2">{chef.credentials}</Typography>
              </Box>
            )}
            {chef.specialties && (
              <Box mt={1}>
                <Typography variant="caption" color="text.secondary">Specialties</Typography>
                <Typography variant="body2">{chef.specialties}</Typography>
              </Box>
            )}
            {chef.rejection_reason && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Rejection reason: {chef.rejection_reason}
              </Alert>
            )}
          </Paper>

          {/* Recipes */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recipes ({recipes.length})
            </Typography>
            {recipes.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Cuisine</TableCell>
                      <TableCell>Prep Time</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipes.map((recipe) => (
                      <TableRow
                        key={recipe.recipe_id}
                        hover
                        onClick={() => navigate(`/admin/recipes/${recipe.recipe_id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{recipe.recipe_id}</TableCell>
                        <TableCell>{recipe.name}</TableCell>
                        <TableCell>{recipe.cuisine || '-'}</TableCell>
                        <TableCell>{recipe.prep_time} min</TableCell>
                        <TableCell>{formatDate(recipe.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recipes attributed yet
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Sidebar - Stats */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Stat Cards */}
          <Stack spacing={2}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Recipe Count</Typography>
                <Typography variant="h4">{dashboard?.recipes.total ?? chef.recipe_count ?? 0}</Typography>
                {dashboard?.recipes.curated != null && (
                  <Typography variant="body2" color="text.secondary">
                    {dashboard.recipes.curated} curated
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Code Uses</Typography>
                <Typography variant="h4">{dashboard?.chef.code_uses ?? chef.code_uses ?? 0}</Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">Meal Plan Appearances</Typography>
                <Typography variant="h4">{dashboard?.usage.total_meal_appearances ?? 0}</Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Chef Application</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{chef.name}</strong>'s application?
          </Typography>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Explain why this application is being rejected..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialogOpen(false); setActionReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend Chef</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to suspend <strong>{chef.name}</strong>? This will deactivate their promo code.
          </Typography>
          <TextField
            label="Suspension Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Explain why this chef is being suspended..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuspendDialogOpen(false); setActionReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuspend}
            disabled={actionLoading || !actionReason}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Suspend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Chef Payment</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Record annual recipe payment for <strong>{chef.name}</strong>.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Year"
              type="number"
              fullWidth
              value={paymentYear}
              onChange={(e) => setPaymentYear(parseInt(e.target.value) || new Date().getFullYear())}
            />
            <TextField
              label="Payment Reference"
              fullWidth
              required
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="e.g., PayPal transaction ID, check number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPaymentDialogOpen(false); setPaymentReference(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRecordPayment}
            disabled={actionLoading || !paymentReference}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChefDetail;
